import { create } from "zustand";

import type { TicketView } from "../lib/customerView/ticketView.js";
import { addPreset, parsePresets, removePreset, type ViewPreset } from "../lib/customerView/viewPresets.js";

/**
 * Filter-Presets der Ticketliste, je Browser in localStorage unter
 * `msc-cv-view-presets` (v1). Warum nicht am Konto: s. `lib/customerView/viewPresets.ts`.
 */
const STORAGE_KEY = "msc-cv-view-presets";

function load(): ViewPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && (parsed as { v?: unknown }).v === 1) {
      return parsePresets((parsed as { items?: unknown }).items);
    }
  } catch {
    /* beschädigter oder gesperrter Speicher — dann eben keine Presets */
  }
  return [];
}

function persist(items: ViewPreset[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, items }));
  } catch {
    /* Quota oder gesperrter Speicher — die Sitzung behält die Liste */
  }
}

interface ViewPresetsState {
  presets: ViewPreset[];
  /** Speichert die Leiste unter `name`; gleicher Name ersetzt. Liefert den gespeicherten Eintrag. */
  save: (name: string, view: TicketView) => ViewPreset | null;
  remove: (id: string) => void;
}

export const useViewPresetsStore = create<ViewPresetsState>((set, get) => ({
  presets: load(),
  save: (name, view) => {
    const next = addPreset(get().presets, name, view);
    if (next === get().presets) return null;
    set({ presets: next });
    persist(next);
    return next.find((p) => p.name === name.trim().slice(0, 60)) ?? next[next.length - 1] ?? null;
  },
  remove: (id) => {
    const next = removePreset(get().presets, id);
    set({ presets: next });
    persist(next);
  },
}));
